import { getServerUrl } from './updateChannel';
import { getLocalApiOrigin } from './localApi';
import { isWeb, webClientVersion } from './platform';

// Cached current client version. The version doesn't change at runtime.
// a successful auto-update relaunches the process. So a single fetch
// from the local Electron server covers every caller.
let cachedClientVersion = null;
let inflightVersionFetch = null;

export async function ensureClientVersion() {
  if (cachedClientVersion) return cachedClientVersion;
  // Web builds have no updater endpoint; the version is baked in at build
  // time. Without it the server rejects every WS connect (close 4001).
  if (isWeb()) {
    cachedClientVersion = webClientVersion();
    return cachedClientVersion;
  }
  if (inflightVersionFetch) return inflightVersionFetch;
  inflightVersionFetch = (async () => {
    try {
      const res = await fetch(`${getLocalApiOrigin()}/api/update/status`);
      if (!res.ok) return null;
      const body = await res.json();
      cachedClientVersion = body?.currentVersion || null;
      return cachedClientVersion;
    } catch {
      return null;
    } finally {
      inflightVersionFetch = null;
    }
  })();
  return inflightVersionFetch;
}

export function getCachedClientVersion() {
  return cachedClientVersion;
}

// Subscribers receive { ok, minClientVersion, serverVersion, reason }
// whenever the gate flips state, plus once on subscribe if there's a
// known result. Drives the forced-update modal in app.jsx.
const subscribers = new Set();
let lastResult = null;

function invokeListener(cb, result) {
  try { cb(result); } catch (err) { console.error('[versionCheck] listener error:', err); }
}

export function onVersionCheck(callback) {
  subscribers.add(callback);
  if (lastResult) invokeListener(callback, lastResult);
  return () => subscribers.delete(callback);
}

function notify(result) {
  // Only fire when the gate flips (ok→fail, fail→ok). The REST poll
  // would otherwise spam listeners every cycle even when nothing changed.
  const prev = lastResult;
  lastResult = result;
  if (prev && prev.ok === result.ok) return;
  for (const cb of subscribers) invokeListener(cb, result);
}

export async function checkVersionNow() {
  const clientVersion = await ensureClientVersion();
  if (!clientVersion) return null;
  try {
    const url = `${getServerUrl()}/version/check?clientVersion=${encodeURIComponent(clientVersion)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const body = await res.json();
    notify(body);
    return body;
  } catch {
    return null;
  }
}

// Called via WS message `version:rejected`. Feeds the same subscriber
// pipeline as the REST poll so the modal reacts identically regardless
// of which gate fired.
export function reportWebSocketRejection(payload) {
  notify({ ok: false, ...payload });
}

let pollTimer = null;
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function startVersionPolling() {
  if (pollTimer) return;
  checkVersionNow();
  pollTimer = setInterval(checkVersionNow, POLL_INTERVAL_MS);
}

export function stopVersionPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}
